import {
  emptyWorkspace,
  type Assignment,
  type BookingSegment,
  type Person,
  type Unit,
  type Workspace,
} from "./model";
import { createDemoWorkspace, demoServices } from "./fixtures";
import { defaultRule, evaluateRules } from "./rules";
import {
  addDays,
  localDate,
  localToInstant,
  stableId,
  todayAt,
  validDate,
  validTime,
} from "./time";
import { validateWorkspace } from "./commands";

// Deliberately isolated legacy DTO boundary. No runtime feature depends on old identities or snapshots.
type Legacy = Record<string, any>;
function decode(
  raw: string | null,
  label: string,
  archive: unknown[],
): unknown {
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    archive.push({ type: "unreadable-legacy-data", label, raw });
    return null;
  }
}
const array = (value: unknown): Legacy[] =>
  Array.isArray(value)
    ? value.filter((item) => item && typeof item === "object")
    : [];
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
const serviceId = (kind: unknown) =>
  /window/i.test(String(kind))
    ? "windows"
    : /terrace/i.test(String(kind))
      ? "terrace"
      : /deep/i.test(String(kind))
        ? "deep-clean"
        : /inspect/i.test(String(kind))
          ? "inspection"
          : /rework/i.test(String(kind))
            ? "rework"
            : /linen/i.test(String(kind))
              ? "linen"
              : "cleaning";

/** A one-time conversion, preserving exact originals in the repository transaction. */
export function migrateLegacy(
  operationsRaw: string | null,
  shiftsRaw: string | null,
): Workspace {
  if (operationsRaw === null && shiftsRaw === null)
    return createDemoWorkspace();
  const state = emptyWorkspace();
  state.services = demoServices();
  const archive = state.legacyArchive;
  const parsedOperations = decode(operationsRaw, "operations", archive);
  const operations: Legacy =
    parsedOperations &&
    typeof parsedOperations === "object" &&
    !Array.isArray(parsedOperations)
      ? (parsedOperations as Legacy)
      : {};
  const shifts = array(decode(shiftsRaw, "shifts", archive));
  const unitMap = new Map<string, string>();
  const personMap = new Map<string, string>();
  const taskMap = new Map<string, string>();
  const scopedPeople = new Map<string, Map<string, string>>();
  const operationAssignmentMap = new Map<string, string>();
  const operationsUnits = array(operations.units);
  const operationsAssignments = array(operations.assignments);
  const defaultPropertyName =
    shifts.find((item) => typeof item.property === "string")?.property ??
    "Holiday Club Katinkulta";
  const property = (name: unknown) => {
    const label =
      typeof name === "string" && name.trim()
        ? name.trim()
        : defaultPropertyName;
    const id = stableId("property", label);
    if (!state.properties.some((item) => item.id === id))
      state.properties.push({ id, name: label, timeZone: "Europe/Helsinki" });
    return id;
  };
  const stamp = (
    value: unknown,
    fallbackDate?: string,
    fallbackTime = "08:00",
  ): string | undefined => {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      if (
        /Z$|[+-]\d{2}:\d{2}$/.test(value) &&
        Number.isFinite(Date.parse(value))
      )
        return new Date(value).toISOString();
      if (validDate(value.slice(0, 10)) && validTime(value.slice(11, 16)))
        return localToInstant(
          value.slice(0, 10),
          value.slice(11, 16),
          "Europe/Helsinki",
          false,
          "earlier",
        );
    }
    if (fallbackDate && validDate(fallbackDate))
      return localToInstant(
        fallbackDate,
        typeof value === "string" && validTime(value) ? value : fallbackTime,
        "Europe/Helsinki",
        false,
        "earlier",
      );
    return undefined;
  };
  function unit(house: Legacy, propertyId: string): string {
    const oldId = String(
      house.id ?? house.name ?? stableId("unknown-unit", JSON.stringify(house)),
    );
    const mapKey = `${propertyId}:${oldId}`;
    if (unitMap.has(mapKey)) return unitMap.get(mapKey)!;
    const id = stableId("unit", propertyId, oldId);
    unitMap.set(mapKey, id);
    const record: Unit = {
      id,
      propertyId,
      name: typeof house.name === "string" ? house.name : oldId,
      territory: String(house.district ?? house.territory ?? ""),
      type: "Apartment",
      tags: [
        ...(house.hasPet ? ["Pet stay"] : []),
        ...(house.extraDirty ? ["Extra dirty"] : []),
      ],
      archived: false,
      access:
        house.access === "vacant"
          ? "vacant"
          : house.access === "occupied"
            ? "occupied"
            : "unknown",
      cleanliness: "unknown",
      factUpdatedAt: stamp(house.updatedAt) ?? new Date().toISOString(),
      factSource: String(house.source ?? "Legacy data"),
      disabledInheritedRuleIds: [],
      notes: String(house.note ?? house.notes ?? ""),
      standardMinutes:
        Number.isSafeInteger(house.cleaningStandardMinutes) &&
        house.cleaningStandardMinutes > 0
          ? house.cleaningStandardMinutes
          : 60,
      externalIds: {},
    };
    state.units.push(record);
    if (house.deliveredAt) {
      const at = stamp(house.deliveredAt);
      if (at)
        state.linen.push({
          id: stableId("legacy-linen", id, "deliver", at),
          unitId: id,
          operation: "deliver",
          at,
          actor: record.factSource,
          notes: "Preserved legacy delivery fact.",
        });
    }
    if (house.collectedAt) {
      const at = stamp(house.collectedAt);
      if (at)
        state.linen.push({
          id: stableId("legacy-linen", id, "collect", at),
          unitId: id,
          operation: "collect",
          at,
          actor: record.factSource,
          notes: "Preserved legacy collection fact.",
        });
    }
    return id;
  }
  function person(
    old: Legacy,
    fallbackName?: string,
    scope = "operations",
  ): string {
    const name =
      typeof old.name === "string"
        ? old.name
        : (fallbackName ?? "Unknown employee");
    const oldId = String(old.id ?? stableId("legacy-person", scope, name));
    const existingId = personMap.get(oldId);
    if (existingId) {
      if (state.people.find((item) => item.id === existingId)?.name === name)
        return existingId;
      const quarantineId = stableId("quarantined-person", oldId, scope);
      if (!state.people.some((item) => item.id === quarantineId)) {
        state.people.push({
          id: quarantineId,
          name,
          skills: strings(old.skills),
          duties: old.role ? [String(old.role)] : [],
          archived: true,
          externalIds: {},
        });
        archive.push({
          type: "ambiguous-person-identity",
          oldId,
          canonicalId: quarantineId,
          scope,
          incoming: old,
          reason:
            "Conflicting source identity retained separately; review before using this person in new plans.",
        });
      }
      return quarantineId;
    }
    const id = stableId("person", oldId);
    personMap.set(oldId, id);
    const record: Person = {
      id,
      name,
      skills: strings(old.skills).length ? strings(old.skills) : ["Cleaning"],
      duties: old.role ? [String(old.role)] : ["Cleaner"],
      archived: false,
      externalIds: {},
    };
    state.people.push(record);
    return id;
  }
  for (const old of operationsUnits) unit(old, property(old.property));
  for (const old of array(operations.people)) person(old);
  for (const oldShift of shifts) {
    const propertyId = property(oldShift.property);
    const snapshot = oldShift.planning;
    if (
      !snapshot ||
      !Array.isArray(snapshot.houses) ||
      !Array.isArray(snapshot.people) ||
      !Array.isArray(snapshot.tasks)
    ) {
      if (snapshot)
        archive.push({
          type: "malformed-shift-snapshot",
          shiftId: oldShift.id,
          snapshot,
        });
      continue;
    }
    for (const house of array(snapshot.houses)) unit(house, propertyId);
    const identities = new Map<string, string>();
    for (const employee of array(snapshot.people))
      identities.set(
        String(employee.id),
        person(employee, undefined, String(oldShift.id)),
      );
    scopedPeople.set(String(oldShift.id), identities);
  }
  for (const booking of array(operations.bookings)) {
    const oldUnit = operationsUnits.find((item) => item.id === booking.unitId);
    const propertyId = property(oldUnit?.property);
    const unitId = unitMap.get(`${propertyId}:${booking.unitId}`);
    const arrival = stamp(
      `${booking.arrival}T${booking.arrivalTime || "16:00"}:00`,
    );
    const departure = stamp(
      `${booking.departure}T${booking.departureTime || "11:00"}:00`,
    );
    if (!unitId || !arrival || !departure || departure <= arrival) {
      archive.push({ type: "invalid-booking", booking });
      continue;
    }
    state.bookings.push({
      id: stableId("booking", unitId, String(booking.id)),
      unitId,
      guest: String(booking.guest ?? "Legacy booking"),
      source: String(booking.source ?? "Legacy"),
      externalId: String(booking.id),
      revision: 1,
      status: "confirmed",
      arrival,
      departure,
    });
  }
  const groupIds = [
    ...new Set(
      array(operations.bookings)
        .map((item) => item.groupId)
        .filter(Boolean),
    ),
  ];
  for (const groupId of groupIds) {
    const oldBookings = array(operations.bookings).filter(
      (item) => item.groupId === groupId,
    );
    const bookings = state.bookings
      .filter((item) => oldBookings.some((old) => old.id === item.externalId))
      .sort((a, b) => a.arrival.localeCompare(b.arrival));
    if (
      bookings.length < 2 ||
      bookings.some((item) => item.unitId !== bookings[0].unitId)
    ) {
      archive.push({ type: "invalid-continuity", groupId, oldBookings });
      continue;
    }
    const id = stableId("stay", String(groupId));
    const valid = bookings.every(
      (item, index) =>
        !index ||
        localDate(bookings[index - 1].departure) === localDate(item.arrival),
    );
    // Existing explicit linkage is retained as a manual legacy confirmation, never inferred from names.
    state.stays.push({
      id,
      bookingIds: bookings.map((item) => item.id),
      confirmedBy: "Legacy dispatcher action",
      confirmedAt: new Date().toISOString(),
      reason: "Previously linked explicitly in Luuta.",
      valid,
    });
    bookings.forEach((item) => {
      item.continuityId = id;
    });
  }
  for (const old of array(operations.workdays)) {
    const personId = personMap.get(String(old.personId));
    const start = stamp(old.start, old.date);
    const end = stamp(old.end, old.date);
    if (
      !personId ||
      !start ||
      !end ||
      end <= start ||
      !validDate(String(old.date))
    ) {
      archive.push({ type: "invalid-workday", old });
      continue;
    }
    const overlap = state.availability.some(
      (item) =>
        item.personId === personId && item.start < end && item.end > start,
    );
    if (overlap) {
      archive.push({ type: "overlapping-legacy-workday", old });
      continue;
    }
    state.availability.push({
      id: stableId("availability", String(old.id)),
      personId,
      businessDate: old.date,
      start,
      end,
      source: ["manual", "quinyx", "shift"].includes(old.source)
        ? old.source
        : "manual",
      kind: old.unavailable ? "unavailable" : "work",
      localOverride: old.source === "manual",
    });
  }
  const createAssignment = (
    old: Legacy,
    unitId: string,
    canonicalId: string,
    owningDate?: string,
  ): Assignment => {
    const date = old.date === undefined && owningDate && validDate(owningDate) ? owningDate : validDate(String(old.date)) ? old.date : null;
    if (old.date !== undefined && old.date !== null && !validDate(String(old.date))) archive.push({ type: "invalid-assignment-date", assignmentId: canonicalId, originalDate: old.date, owningDate, reason: "Invalid historical date retained for review; no scheduling date was invented." });
    const deadline = old.dueAt ?? old.deadlineAt ?? old.deadline;
    const dueAt = stamp(deadline) ?? (typeof deadline === "string" && validTime(deadline) && date ? stamp(deadline, date) : undefined);
    if (deadline && !dueAt) archive.push({ type: "invalid-assignment-deadline", assignmentId: canonicalId, original: deadline });
    return ({
    id: canonicalId,
    unitId,
    serviceId: serviceId(old.kind),
    date,
    dueAt,
    ...(dueAt ? { manualOverrides: { dueAt } } : {}),
    earliestStart: stamp(old.earliestStart),
    estimateMinutes:
      Number.isSafeInteger(old.estimatedMinutes) && old.estimatedMinutes > 0
        ? old.estimatedMinutes
        : state.units.find((item) => item.id === unitId)!.standardMinutes,
    requiredSkills: strings(old.requiredSkills).length
      ? strings(old.requiredSkills)
      : state.services.find((item) => item.id === serviceId(old.kind))!
          .requiredSkills,
    preferredSkills: [],
    crewSize: 1,
    priority: old.priority ? "high" : "normal",
    status:
      old.status === "completed"
        ? "completed"
        : old.status === "declined" || old.status === "cancelled"
          ? "declined"
          : old.status === "in-progress" || old.inProgress
            ? "in-progress"
            : "open",
    occurrenceIds: [],
    origin:
      old.origin === "rule" || old.origin === "booking" ? "rule" : "manual",
    notes: String(old.note ?? old.requestedNote ?? ""),
    revision: 1,
    contextKey: `legacy:${canonicalId}`,
    manualDate: Boolean(old.originalDate || old.unscheduledFrom),
    completedAt: stamp(old.actualCompletedAt),
    approval: old.approval,
  });
  };
  for (const old of operationsAssignments) {
    const oldUnit = operationsUnits.find((item) => item.id === old.unitId);
    const owner = shifts.find((item) => item.id === old.shiftId);
    const propertyId = property(owner?.property ?? oldUnit?.property);
    const unitId = unitMap.get(`${propertyId}:${old.unitId}`);
    if (!unitId) {
      archive.push({ type: "unmapped-assignment", old });
      continue;
    }
    const assignment = createAssignment(
      old,
      unitId,
      stableId("assignment", String(old.id)),
      validDate(String(owner?.date)) ? owner?.date : undefined,
    );
    state.assignments.push(assignment);
    operationAssignmentMap.set(String(old.id), assignment.id);
  }
  for (const oldShift of shifts) {
    if (typeof oldShift.id !== "string") {
      archive.push({ type: "invalid-shift", oldShift });
      continue;
    }
    if (!validDate(String(oldShift.date))) {
      archive.push({ type: "invalid-shift-date", oldShift, reason: "The original shift, actual sessions and source date are retained for historical review. No operational date was invented." });
      continue;
    }
    const propertyId = property(oldShift.property);
    const date = validDate(String(oldShift.date)) ? oldShift.date : todayAt();
    const start = stamp(oldShift.start, date)!;
    let end = stamp(oldShift.end, date, "16:00")!;
    if (end <= start)
      end = localToInstant(
        addDays(date, 1),
        validTime(String(oldShift.end)) ? oldShift.end : "16:00",
      );
    const snapshot: Legacy =
      oldShift.planning && Array.isArray(oldShift.planning.tasks)
        ? oldShift.planning
        : {};
    const personIdFor = (oldId: string) =>
      scopedPeople.get(oldShift.id)?.get(oldId) ?? personMap.get(oldId);
    const roster = strings(oldShift.planningRoster);
    const rosterIds: string[] = [];
    for (const employee of array(snapshot.people))
      if (
        roster.includes(employee.id) ||
        strings(oldShift.people).includes(employee.name)
      )
        rosterIds.push(personIdFor(String(employee.id))!);
    for (const name of strings(oldShift.people))
      if (
        !state.people.some(
          (item) => rosterIds.includes(item.id) && item.name === name,
        )
      ) {
        const matches = state.people.filter((item) => item.name === name);
        if (matches.length === 1) rosterIds.push(matches[0].id);
        else {
          const unresolved = person({}, name, `unresolved:${oldShift.id}`);
          state.people.find((item) => item.id === unresolved)!.archived = true;
          rosterIds.push(unresolved);
          archive.push({
            type: "unresolved-name-only-person",
            shiftId: oldShift.id,
            name,
            canonicalId: unresolved,
          });
        }
      }
    const assignmentIds: string[] = [];
    for (const task of array(snapshot.tasks)) {
      const unitId = unitMap.get(`${propertyId}:${task.houseId}`);
      if (!unitId) {
        archive.push({ type: "unmapped-task", shiftId: oldShift.id, task });
        continue;
      }
      const operational = operationsAssignments.find(
        (item) =>
          item.shiftId === oldShift.id &&
          (item.id === task.id ||
            item.id === `legacy-${oldShift.id}-${task.id}`),
      );
      let assignment = operational
        ? state.assignments.find(
            (item) =>
              item.id === operationAssignmentMap.get(String(operational.id)),
          )
        : undefined;
      if (!assignment) {
        assignment = createAssignment(
          task,
          unitId,
          stableId("assignment", oldShift.id, String(task.id)),
          validDate(String(oldShift.date)) ? oldShift.date : undefined,
        );
        state.assignments.push(assignment);
      }
      taskMap.set(`${oldShift.id}:${task.id}`, assignment.id);
      if (
        assignment.date === date &&
        !["cancelled", "declined"].includes(task.status)
      )
        assignmentIds.push(assignment.id);
      for (const [index, session] of array(task.actualSessions).entries()) {
        const began = stamp(session.startedAt);
        const ended = stamp(session.endedAt);
        if (began)
          state.sessions.push({
            id: stableId("session", oldShift.id, task.id, index),
            assignmentId: assignment.id,
            allocationId: stableId("allocation", oldShift.id, assignment.id),
            personIds: strings(session.assigneeIds)
              .map(personIdFor)
              .filter((id): id is string => Boolean(id)),
            start: began,
            ...(ended ? { end: ended } : {}),
          });
      }
      if (strings(task.additionalWork).length) {
        if (task.status === "completed" || array(task.actualSessions).length)
          archive.push({
            type: "bundled-work-history",
            assignmentId: assignment.id,
            tags: task.additionalWork,
            original: task,
          });
        else
          for (const [index, tag] of strings(task.additionalWork).entries()) {
            const extraService = serviceId(tag);
            if (extraService === "cleaning") {
              archive.push({
                type: "legacy-custom-extra",
                assignmentId: assignment.id,
                tag,
              });
              continue;
            }
            const extra = {
              ...createAssignment(
                {
                  ...task,
                  kind: tag,
                  estimatedMinutes:
                    task.additionalWorkMinutes?.[tag] ??
                    state.services.find((item) => item.id === extraService)!
                      .estimateMinutes,
                },
                unitId,
                stableId("extra", oldShift.id, task.id, index),
                validDate(String(oldShift.date)) ? oldShift.date : undefined,
              ),
              origin: "manual" as const,
            };
            state.assignments.push(extra);
            if (extra.date === date) assignmentIds.push(extra.id);
          }
      }
    }
    for (const old of operationsAssignments.filter(
      (item) => item.shiftId === oldShift.id,
    )) {
      const aid = operationAssignmentMap.get(String(old.id));
      if (
        aid &&
        state.assignments.find((item) => item.id === aid)?.date === date &&
        !assignmentIds.includes(aid)
      )
        assignmentIds.push(aid);
    }
    const status = [
      "Draft",
      "Planned",
      "Active",
      "Completed",
      "Cancelled",
    ].includes(oldShift.status)
      ? oldShift.status
      : "Draft";
    const positions = [...new Set(rosterIds)].map((personId) => ({
      id: stableId("position", oldShift.id, personId),
      personId,
      requiredSkills: [],
      duty:
        state.people.find((item) => item.id === personId)?.duties[0] ??
        "Cleaner",
      coverageStart: start,
      coverageEnd: end,
    }));
    state.shifts.push({
      id: oldShift.id,
      name:
        typeof oldShift.comment === "string" && oldShift.comment.trim()
          ? oldShift.comment
          : String(oldShift.name ?? "Imported shift"),
      propertyId,
      date,
      start,
      end,
      status,
      assignmentIds,
      positions,
      revision: 1,
      comment: String(oldShift.comment ?? ""),
      ...(typeof oldShift.startedAt === "number"
        ? { startedAt: new Date(oldShift.startedAt).toISOString() }
        : {}),
    });
    for (const assignmentId of assignmentIds) {
      const assignment = state.assignments.find(
        (item) => item.id === assignmentId,
      )!;
      const task = array(snapshot.tasks).find(
        (item) => taskMap.get(`${oldShift.id}:${item.id}`) === assignmentId,
      );
      const participants = strings(task?.assignees)
        .map(personIdFor)
        .filter((id): id is string => Boolean(id));
      const plannedStart = task?.plannedStart
        ? stamp(task.plannedStart, date)
        : undefined;
      const plannedEnd = task?.plannedEnd
        ? stamp(task.plannedEnd, date)
        : undefined;
      const timing =
        plannedStart && plannedEnd && plannedEnd > plannedStart
          ? { start: plannedStart, end: plannedEnd }
          : {};
      if (plannedStart && !Object.keys(timing).length)
        archive.push({
          type: "legacy-timing-needs-review",
          assignmentId,
          task,
        });
      state.allocations.push({
        id: stableId("allocation", oldShift.id, assignmentId),
        assignmentId,
        shiftId: oldShift.id,
        participantIds: participants,
        status:
          status === "Draft"
            ? "tentative"
            : status === "Completed"
              ? "finished"
              : status === "Cancelled"
                ? "released"
                : "committed",
        revision: 1,
        ...timing,
      });
    }
    for (const employee of array(snapshot.people)) {
      const personId = personIdFor(String(employee.id));
      if (!personId || !rosterIds.includes(personId)) continue;
      if (
        !state.availability.some(
          (item) => item.personId === personId && item.businessDate === date,
        )
      ) {
        const began = stamp(employee.start, date) ?? start;
        const ended = stamp(employee.end, date) ?? end;
        if (ended > began)
          state.availability.push({
            id: stableId("availability", oldShift.id, personId),
            personId,
            businessDate: date,
            start: began,
            end: ended,
            source: "shift",
          });
      }
      const attendance = employee.attendance?.[date];
      if (attendance)
        state.attendance.push({
          id: stableId("attendance", oldShift.id, personId),
          personId,
          shiftId: oldShift.id,
          status:
            attendance === "present"
              ? "present"
              : attendance === "unavailable"
                ? "absent"
                : attendance === "confirmed"
                  ? "confirmed"
                  : "expected",
          at: start,
        });
    }
    for (const inspection of array(snapshot.inspections)) {
      const unitId = unitMap.get(`${propertyId}:${inspection.houseId}`);
      if (!unitId) continue;
      const assignment = createAssignment(
        {
          ...inspection,
          kind: "Quality inspection",
          estimatedMinutes: snapshot.inspectionMinutes ?? 15,
        },
        unitId,
        stableId("inspection", oldShift.id, inspection.id),
        oldShift.date,
      );
      const source = taskMap.get(`${oldShift.id}:${inspection.sourceTaskId}`);
      assignment.dependsOn = source ? [source] : [];
      assignment.origin = "inspection";
      state.assignments.push(assignment);
      if (!inspection.superseded && inspection.status !== "cancelled") {
        state.shifts
          .find((item) => item.id === oldShift.id)!
          .assignmentIds.push(assignment.id);
        state.allocations.push({
          id: stableId("allocation", oldShift.id, assignment.id),
          assignmentId: assignment.id,
          shiftId: oldShift.id,
          participantIds: strings(inspection.assignees)
            .map(personIdFor)
            .filter((id): id is string => Boolean(id)),
          status:
            status === "Completed"
              ? "finished"
              : status === "Draft"
                ? "tentative"
                : "committed",
          revision: 1,
        });
      }
      for (const [index, session] of array(
        inspection.actualSessions,
      ).entries()) {
        const began = stamp(session.startedAt);
        const ended = stamp(session.endedAt);
        if (began)
          state.sessions.push({
            id: stableId(
              "inspection-session",
              oldShift.id,
              inspection.id,
              index,
            ),
            assignmentId: assignment.id,
            personIds: strings(session.assigneeIds)
              .map(personIdFor)
              .filter((id): id is string => Boolean(id)),
            start: began,
            ...(ended ? { end: ended } : {}),
          });
      }
    }
    archive.push({
      type: "legacy-shift-source",
      shiftId: oldShift.id,
      source: oldShift,
    });
  }
  const today = todayAt();
  for (const propertyRecord of state.properties) {
    const rule = {
      ...defaultRule(propertyRecord.id, today),
      id: stableId("rule", propertyRecord.id, "turnover"),
      name: "Inherited turnover cleaning",
      status: "active" as const,
    };
    state.rules.push(rule);
    for (const canonicalUnit of state.units.filter(
      (item) => item.propertyId === propertyRecord.id,
    )) {
      const old = operationsUnits.find(
        (item) =>
          unitMap.get(`${propertyRecord.id}:${item.id}`) === canonicalUnit.id,
      );
      if (
        old?.cleaningRule === "none" ||
        old?.cleaningRule === "before-checkin"
      ) {
        canonicalUnit.disabledInheritedRuleIds.push(rule.id);
        canonicalUnit.disabledRuleReasons = {
          [rule.id]: "Preserved previous unit rule choice.",
        };
      }
      if (old?.cleaningRule === "before-checkin")
        state.rules.push({
          ...defaultRule(canonicalUnit.id, today, "unit"),
          id: stableId("rule", canonicalUnit.id, "arrival"),
          name: "Prepare before arrival",
          status: "active",
          trigger: { ...rule.trigger, kind: "checkin" },
          window: {
            ...rule.window,
            earliestOffsetHours: -24,
            dueOffsetHours: 0,
          },
        });
    }
  }
  const desired = evaluateRules(state, { from: today, to: addDays(today, 42) });
  const matched = new Set<string>();
  for (const generated of desired.assignments) {
    const candidates = state.assignments.filter(
      (item) =>
        !matched.has(item.id) &&
        item.unitId === generated.unitId &&
        item.serviceId === generated.serviceId &&
        item.date === generated.date,
    );
    const assignment =
      candidates.find((item) => item.origin === "rule") ??
      (candidates.length === 1 ? candidates[0] : undefined);
    if (assignment) {
      matched.add(assignment.id);
      assignment.occurrenceIds = generated.occurrenceIds;
      assignment.contextKey = generated.contextKey;
      assignment.earliestStart ??= generated.earliestStart;
      assignment.dueAt ??= generated.dueAt;
      for (const occurrence of desired.occurrences.filter(
        (item) => item.assignmentId === generated.id,
      )) {
        occurrence.assignmentId = assignment.id;
        if (assignment.status === "completed") occurrence.status = "satisfied";
        if (assignment.status === "declined") occurrence.status = "suppressed";
      }
    } else state.assignments.push(generated);
  }
  state.occurrences = desired.occurrences;
  state.integrationPolicies.hotellinx = {
    enabled: operations.integrations?.hotellinx?.enabled !== false,
    cleaningSync: ["checked-out", "always", "never"].includes(
      operations.integrations?.hotellinx?.cleaningSync,
    )
      ? operations.integrations.hotellinx.cleaningSync
      : "checked-out",
  };
  state.audit.push({
    id: "migration-v1",
    type: "workspace.migrate",
    actor: "Luuta migration",
    at: new Date().toISOString(),
    summary:
      "Legacy data migrated once. Exact originals backed up; ambiguous records retained for review.",
    revision: 0,
  });
  // Repeated references can occur in malformed old data; preserve source and quarantine the duplicate commitment.
  const committed = new Set<string>();
  state.allocations = state.allocations.map((item) => {
    if (item.status !== "committed") return item;
    if (!committed.has(item.assignmentId)) {
      committed.add(item.assignmentId);
      return item;
    }
    archive.push({ type: "duplicate-legacy-allocation", allocation: item });
    return { ...item, status: "released" };
  });
  validateWorkspace(state);
  return state;
}
